import { useEffect, useState } from 'react';
import { useOrg } from '../context/OrgContext';
import { getAdobeAccessToken, getImsUserId } from '../auth/adobeIms';
const PENDING_SWITCH_KEY = 'imsOrgSwitch_pending';
interface PendingSwitch {
  imsOrgId: string;
  destinationUserId: string;
}

// Find which cluster userId owns the given org (first match wins).
function resolveTargetUserId(imsOrgId: string, profileOrgMap: Record<string, string[]>): string | null {
  for (const [userId, orgIds] of Object.entries(profileOrgMap)) {
    if (orgIds.includes(imsOrgId)) return userId;
  }
  return null;
}
function Spinner() {
  return <svg width={12} height={12} viewBox="0 0 24 24" className="animate-spin" style={{
    color: 'rgba(255,255,255,0.5)'
  }} data-id="element-186">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-187" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-188" />
    </svg>;
}
export function OrgSelector() {
  const {
    orgs,
    profileOrgMap,
    selectedOrg,
    setSelectedOrg,
    isLoadingOrgs
  } = useOrg();
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  // On mount: detect a post-redirect finalization.
  // After switchProfile() the IMS lib redirects back and fires onAccessToken.
  // At that point the app remounts; we clear the pending marker here.
  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_SWITCH_KEY);
    if (!raw || !getAdobeAccessToken()) return;
    try {
      const pending = JSON.parse(raw) as PendingSwitch;
      // New IMS session is active — clear the pending marker.
      // The OrgContext will re-fetch the catalog with the new token automatically.
      sessionStorage.removeItem(PENDING_SWITCH_KEY);

      // Find and select the org the user switched to, if it's in the new catalog.
      const targetOrg = orgs.find(o => o.imsOrgId === pending.imsOrgId);
      if (targetOrg) setSelectedOrg(targetOrg);
    } catch {
      sessionStorage.removeItem(PENDING_SWITCH_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  if (isLoadingOrgs) {
    return <span style={{
      fontSize: 11,
      color: 'rgba(255,255,255,0.5)',
      padding: '4px 8px'
    }} data-id="element-189">
        Loading orgs…
      </span>;
  }
  if (!orgs.length) return null;
  if (orgs.length === 1) {
    return <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      background: 'rgba(255,255,255,0.1)',
      borderRadius: 8,
      padding: '3px 10px',
      fontSize: 11,
      color: 'rgba(255,255,255,0.85)'
    }} data-id="element-190">
        <span style={{
        opacity: 0.6
      }} data-id="element-191">Org:</span>
        <span style={{
        fontWeight: 600
      }} data-id="element-192">{selectedOrg?.orgName ?? orgs[0].orgName}</span>
      </div>;
  }
  async function handleOrgChange(imsOrgId: string) {
    const org = orgs.find(o => o.imsOrgId === imsOrgId);
    if (!org) return;

    // Always update app-local selection immediately.
    setSelectedOrg(org);
    setSwitchError(null);

    // Determine if the chosen org belongs to a different IMS account (T2E / cluster).
    const destinationUserId = resolveTargetUserId(imsOrgId, profileOrgMap);
    const currentUserId = getImsUserId();

    // No cross-account switch needed — org is in the current IMS session.
    if (!destinationUserId || !currentUserId || destinationUserId === currentUserId) return;
    if (!window.adobeIMS) return;
    try {
      setSwitching(true);

      // Purge the current token/profile before switching so IMSLib starts clean.
      const ims = window.adobeIMS as Record<string, unknown>;
      if (typeof ims.purgeTokenAndProfile === 'function') {
        (ims.purgeTokenAndProfile as () => void)();
      } else if (ims.tokenService && typeof (ims.tokenService as Record<string, unknown>).purge === 'function') {
        ((ims.tokenService as Record<string, unknown>).purge as () => void)();
      }

      // Persist the intent across the IMS redirect.
      const pending: PendingSwitch = {
        imsOrgId,
        destinationUserId
      };
      sessionStorage.setItem(PENDING_SWITCH_KEY, JSON.stringify(pending));

      // Trigger the real IMS session/profile switch — this will redirect.
      if (typeof ims.switchProfile === 'function') {
        await (ims.switchProfile as (profile: unknown, opts: unknown) => Promise<void>)(destinationUserId,
        //{ userId: destinationUserId },
        {
          explicit_profile_switch: true
        });
      }
    } catch (err) {
      sessionStorage.removeItem(PENDING_SWITCH_KEY);
      setSwitchError(err instanceof Error ? err.message : 'Profile switch failed');
      setSwitching(false);
    }
  }
  return <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6
  }} data-id="element-193">
      <span style={{
      fontSize: 11,
      color: 'rgba(255,255,255,0.55)'
    }} data-id="element-194">Org:</span>
      <select value={selectedOrg?.imsOrgId ?? ''} disabled={switching} onChange={e => void handleOrgChange(e.target.value)} style={{
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.25)',
      borderRadius: 8,
      color: '#fff',
      fontSize: 11,
      fontWeight: 600,
      padding: '3px 8px',
      cursor: switching ? 'wait' : 'pointer',
      maxWidth: 200
    }} data-id="element-195">
        {!selectedOrg && <option value="" disabled data-id="element-196">Select org…</option>}
        {orgs.map(o => <option key={o.imsOrgId} value={o.imsOrgId} style={{
        background: '#253044',
        color: '#fff'
      }} data-id="element-197">
            {o.orgName}
          </option>)}
      </select>

      {switching && <span style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 10,
      color: 'rgba(255,255,255,0.5)'
    }} data-id="element-198">
          <Spinner data-id="element-199" /> Switching…
        </span>}
      {switchError && <span style={{
      fontSize: 10,
      color: '#f87171'
    }} data-id="element-200">{switchError}</span>}
    </div>;
}